import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  IconButton,
  InputAdornment,
  InputBase,
  Popper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useEffect, useRef, useState } from 'react';
import C from '../../../../../shared/constants/general';
import { AuthContext } from '../../context/AuthContext';
import ChatBubble from './ChatBubble';
import ParticipantPopup from './ParticipantPopup';
import UserAvatar from './UserAvatar';

const useStyles = makeStyles((theme) => ({
  moderatorPopupBg: {
    backgroundColor: theme.palette.background.default,
    border: theme.palette.text.secondary,
    borderRadius: 3,
  },
  margins: {
    marginRight: '0.5rem',
  },
  commentHolder: {
    backgroundColor: theme.palette.background.shadedown_20,
  },
  commentField: {
    paddingLeft: 10,
    paddingRight: 0,
    backgroundColor: theme.palette.background.default,
    borderRadius: '20px',
  },
  commentAvatar: {
    width: 30,
    height: 30,
    marginLeft: 5,
  },
  commentList: {
    position: 'relative',
    backgroundColor: theme.palette.background.shadedown_20,
    overflowY: 'auto',
  },
  lostConnection: {
    position: 'sticky',
    zIndex: 2,
    top: 0,
    left: 0,
    right: 0,
    height: 22,
    backgroundColor: theme.palette.background.shadedown_10,
    // color: '#fff',
  },
  emojiOpen: {
    padding: 5,
  },
  emojiHolder: {
    backgroundColor: theme.palette.background.default,
    padding: 5,
    borderRadius: 4,
  },
  emojiButton: {
    padding: 3,
    fontSize: '1.3rem',
    lineHeight: '1.7rem',
    width: 28,
    minWidth: 28,
    display: 'block',
  },
}));

const emojiList = [0x1f44d, 0x1f64c, 0x1f9e1, 0x1f600, 0x1f609, 0x1f62e];
const Chat = (props) => {
  const classes = useStyles();
  const listRef = useRef();
  const inputRef = useRef();
  const { authState } = useContext(AuthContext); // Hämta Context
  const [popperOpen, setPopperOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [messageText, setMessageText] = useState('');
  const [dialogState, setDialogState] = useState({
    open: false,
    messageId: null,
    messageText: null,
  });
  const userModalRef = useRef();

  const canModerate =
    props.postModerate &&
    authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR;
  const isModeratorOrSpeaker =
    canModerate ||
    authState.user.participation_level ===
      C.VISITOR.PARTICIPATION_LEVEL.CONTRIBUTE;
  const isSmallScreen = props.reverse;

  const userPopup = (visitor) => {
    userModalRef.current.setState({ open: true, user: visitor });
  };

  const sendMessage = () => {
    if (messageText.length > 0) {
      props.postHandler(messageText);
      setMessageText('');
    }
  };
  const onFormSubmit = (e) => {
    e.preventDefault();
    sendMessage();
    // send to server with e.g. `window.fetch`
  };
  const watchKeypress = (e) => {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  const addEmoji = (emoji) => {
    setMessageText((prev) => `${prev}${String.fromCodePoint(emoji)}`);
    setPopperOpen(false);

    inputRef.current.focus();
  };
  const handleInputChange = (e) => {
    setMessageText(e.target.value);
  };
  const handleDialogClose = () => {
    setDialogState({ open: false });
  };
  const moderate = (messageId, messageText, isMine) => {
    setDialogState({
      open: true,
      isMine: isMine,
      messageId: messageId,
      messageText:
        messageText.length > 60
          ? messageText.substring(0, 60) + '...'
          : messageText,
    });
  };
  const handleEmojiOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setPopperOpen(!popperOpen);
  };

  const moderateRemove = () => {
    props.postModerate(dialogState.messageId, dialogState.isMine);
    setDialogState({ open: false });
  };

  const sendToAsk = () => {
    props.messageToAsk(dialogState.messageId);
    setDialogState({ open: false });
  };

  useEffect(() => {
    listRef.current.scrollTop = props.reverse
      ? 0
      : listRef.current.scrollHeight;
  }, [
    props.list.length,
    listRef.current !== undefined && listRef.current.scrollHeight,
  ]);

  const orderedList = () => {
    return props.reverse ? props.list.slice(0) : props.list.slice(0).reverse();
  };

  return (
    <React.Fragment>
      <ParticipantPopup ref={userModalRef} />
      <Popper
        open={popperOpen}
        anchorEl={anchorEl}
        placement="top-end"
        transition
      >
        <Box className={classes.emojiHolder}>
          {emojiList.map((emoji) => (
            <Button
              className={classes.emojiButton}
              key={String.fromCodePoint(emoji)}
              onClick={() => addEmoji(emoji)}
            >
              {String.fromCodePoint(emoji)}
            </Button>
          ))}
        </Box>
      </Popper>
      <Box display="flex" flexDirection="column" className={props.className}>
        <Box
          ref={listRef}
          className={classes.commentList}
          flexGrow={1}
          order={props.reverse ? 2 : 0}
          pt={1}
        >
          {props.list.length === 0 ? (
            <Box align="center" p={2}>
              {props.progress ? (
                <Box>
                  <CircularProgress size={20} thickness={2} color="inherit" />
                </Box>
              ) : (
                <Typography variant="body2">
                  {props.channelGreetings &&
                  props.channelGreetings[props.channel]
                    ? props.channelGreetings[props.channel]
                    : props.channel === 'moderator'
                    ? window.LANG.ASK_TEXT
                    : window.LANG.CHAT_HI}
                </Typography>
              )}
            </Box>
          ) : (
            orderedList().map((message, index) => {
              return message.tempText ? (
                <ChatBubble key={index} tempText={message.tempText} />
              ) : (
                <ChatBubble
                  userPopup={userPopup}
                  canModerate={canModerate}
                  moderate={props.postModerate ? moderate : null}
                  key={message.id}
                  user={authState.user}
                  message={message}
                />
              );
            })
          )}
          {props.channel === 'moderator' && isModeratorOrSpeaker && (
            <Box align="center">{window.LANG.ASK_ONLY}</Box>
          )}
          {props.channel === 'studio' && isModeratorOrSpeaker && (
            <Box align="center">{window.LANG.CHAT_STUDIO}</Box>
          )}
        </Box>
        {!props.connected && !props.progress && (
          <Box className={classes.lostConnection} order={1} px={2}>
            <Typography variant="body2">Lost connection..</Typography>
          </Box>
        )}
        <Box
          className={classes.commentHolder}
          display="flex"
          alignItems="center"
          py={props.reverse ? 1 : 0}
          order={props.reverse ? 0 : 2}
        >
          <Box p={0.5}>
            <UserAvatar
              className={classes.commentAvatar}
              user={props.user}
              size="small"
            />
          </Box>
          <Box flexGrow={1} p={0.5}>
            {props.disabled ? (
              <Box p={1}>Posting closed</Box>
            ) : (
              <form onSubmit={onFormSubmit}>
                <InputBase
                  value={messageText}
                  endAdornment={
                    <InputAdornment position="end">
                      {isSmallScreen ? (
                        <IconButton
                          className={classes.emojiOpen}
                          color="primary"
                          aria-label="Send"
                          onClick={sendMessage}
                        >
                          <Icon>send</Icon>
                        </IconButton>
                      ) : (
                        <IconButton
                          className={classes.emojiOpen}
                          color="primary"
                          aria-label="Add Emoji"
                          onClick={handleEmojiOpen}
                        >
                          <Icon>sentiment_very_satisfied</Icon>
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                  multiline={true}
                  maxRows={3}
                  onKeyPress={watchKeypress}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Comment..."
                  id="comment"
                  name="comment"
                  inputProps={{ 'aria-label': 'Comment...', ref: inputRef }}
                  className={classes.commentField}
                />
              </form>
            )}
          </Box>
        </Box>
      </Box>
      <Dialog
        PaperProps={{ className: classes.moderatorPopupBg }}
        open={dialogState.open}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogState.isMine ? 'Remove your message?' : 'Moderate message?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogState.messageText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="default">
            cancel
          </Button>
          {!dialogState.isMine && (
            <Button
              startIcon={<Icon>pan_tool</Icon>}
              onClick={sendToAsk}
              color="secondary"
            >
              Send To Ask
            </Button>
          )}
          <Button
            startIcon={<Icon>delete</Icon>}
            onClick={moderateRemove}
            color="primary"
            autoFocus
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default Chat;
